// src/components/StockStatusBadges.jsx
//
// Three small badges that sit in the bot card header for stock_equity bots:
//
//   <PdtBadge daytradeCount={N} pdtBlocked={bool} />
//   <MarketHoursBadge marketStatus="regular"|"pre"|"post"|"closed"|"holiday" />
//   <HaltBadge symbol={"AAPL"} haltedSymbols={[...]} />
//
// All three render NOTHING unless the relevant context applies, so they're
// safe to drop into a bot card row regardless of asset class — they
// self-gate when the data isn't there.

import React from 'react';
import { Chip, Tooltip, Box, Typography } from '@mui/material';
import {
  AccountBalance,
  Schedule,
  Block,
  WarningAmber,
  CheckCircle,
  Pause,
} from '@mui/icons-material';


// ────────────────────────────────────────────────────────────────────────────
// PDT Badge
// ────────────────────────────────────────────────────────────────────────────
// Shows day-trade count (0-4 in a 5-day rolling window).
// Colors:
//   0-2 day trades → quiet gray
//   3 day trades   → amber warning (one away from PDT trigger)
//   4 day trades   → red blocked  (account flagged, blocked from opening new positions)
//
// If pdtBlocked is true, the badge becomes a hard "PDT BLOCKED" chip
// regardless of count, because the account is locked even if the user
// somehow has count < 4 (manual flag).

export function PdtBadge({ daytradeCount, pdtBlocked, equity }) {
  // Self-gate: no PDT data means we're not a stock bot — render nothing.
  if (daytradeCount == null && !pdtBlocked) return null;

  const count = Number(daytradeCount || 0);
  const isAboveMin = Number.isFinite(equity) && equity >= 25000;

  if (pdtBlocked) {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" fontWeight={600}>PDT Restriction Active</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Account flagged as Pattern Day Trader. New day trades blocked
              until equity exceeds $25,000 or restriction is lifted.
            </Typography>
          </Box>
        }
        arrow
      >
        <Chip
          size="small"
          icon={<Block sx={{ fontSize: 14 }} />}
          label="PDT BLOCKED"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: '#2a0a0a',
            color: '#d97b72',
            border: '1px solid #991b1b',
            '& .MuiChip-icon': { color: '#d97b72' },
          }}
        />
      </Tooltip>
    );
  }

  // Above $25k → PDT rule doesn't apply, count is informational only
  if (isAboveMin) {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" fontWeight={600}>Day Trades: {count}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Account equity above $25,000 — PDT rule does not apply.
              Unlimited day trades allowed.
            </Typography>
          </Box>
        }
        arrow
      >
        <Chip
          size="small"
          icon={<CheckCircle sx={{ fontSize: 14 }} />}
          label={`${count} DT`}
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 600,
            bgcolor: '#052e16',
            color: '#46b88a',
            border: '1px solid #065f46',
            '& .MuiChip-icon': { color: '#46b88a' },
          }}
        />
      </Tooltip>
    );
  }

  // Below $25k → PDT rule applies, count matters
  const config = (() => {
    if (count >= 4) return {
      bg: '#2a0a0a', fg: '#d97b72', border: '#991b1b',
      tooltip: 'You have reached 4 day trades in 5 days. Next day trade will trigger PDT restriction.',
    };
    if (count === 3) return {
      bg: '#451a03', fg: '#d9a441', border: '#92400e',
      tooltip: 'You have 3 day trades in 5 days. One more will trigger PDT restriction (account below $25k).',
    };
    return {
      bg: '#0f1218', fg: '#9aa6bd', border: 'rgba(255,255,255,0.1)',
      tooltip: `${count} day trade${count === 1 ? '' : 's'} in last 5 days. PDT rule: max 3 day trades for accounts under $25,000.`,
    };
  })();

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600}>Day Trade Counter</Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {config.tooltip}
          </Typography>
        </Box>
      }
      arrow
    >
      <Chip
        size="small"
        icon={<AccountBalance sx={{ fontSize: 14 }} />}
        label={`${count}/3 DT`}
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: config.bg,
          color: config.fg,
          border: `1px solid ${config.border}`,
          '& .MuiChip-icon': { color: config.fg },
        }}
      />
    </Tooltip>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Market Hours Badge
// ────────────────────────────────────────────────────────────────────────────
// Shows current US equity market state. Backend's marketCalendar publishes
// one of: 'regular', 'pre', 'post', 'closed', 'holiday'.
//
// Pre-market: 4:00am - 9:30am ET
// Regular:    9:30am - 4:00pm ET
// Post:       4:00pm - 8:00pm ET
// Closed:     8:00pm - 4:00am ET (and all of weekend)
// Holiday:    NYSE holiday (full or early close)

export function MarketHoursBadge({ marketStatus, nextOpen, nextClose }) {
  if (!marketStatus) return null;

  const config = {
    regular: {
      label: 'REGULAR HOURS',
      icon: <CheckCircle sx={{ fontSize: 14 }} />,
      bg: '#052e16',
      fg: '#46b88a',
      border: '#065f46',
      tooltip: nextClose ? `Market closes ${formatRelativeTime(nextClose)}` : 'US equity market is open',
    },
    pre: {
      label: 'PRE-MARKET',
      icon: <Schedule sx={{ fontSize: 14 }} />,
      bg: '#1e2a4a',
      fg: '#4d8fc7',
      border: '#4d8fc7',
      tooltip: 'Pre-market session (4:00am-9:30am ET). Extended-hours bots can trade.',
    },
    post: {
      label: 'AFTER HOURS',
      icon: <Schedule sx={{ fontSize: 14 }} />,
      bg: '#1e2a4a',
      fg: '#4d8fc7',
      border: '#4d8fc7',
      tooltip: 'After-hours session (4:00pm-8:00pm ET). Extended-hours bots can trade.',
    },
    closed: {
      label: 'CLOSED',
      icon: <Pause sx={{ fontSize: 14 }} />,
      bg: 'rgba(255,255,255,0.06)',
      fg: '#9aa6bd',
      border: 'rgba(255,255,255,0.1)',
      tooltip: nextOpen ? `Market opens ${formatRelativeTime(nextOpen)}` : 'Market is closed',
    },
    holiday: {
      label: 'HOLIDAY',
      icon: <WarningAmber sx={{ fontSize: 14 }} />,
      bg: '#451a03',
      fg: '#d9a441',
      border: '#92400e',
      tooltip: nextOpen ? `Market holiday. Reopens ${formatRelativeTime(nextOpen)}` : 'NYSE holiday',
    },
  };

  const c = config[marketStatus] || config.closed;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600}>US Equity Market</Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {c.tooltip}
          </Typography>
        </Box>
      }
      arrow
    >
      <Chip
        size="small"
        icon={c.icon}
        label={c.label}
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: c.bg,
          color: c.fg,
          border: `1px solid ${c.border}`,
          '& .MuiChip-icon': { color: c.fg },
        }}
      />
    </Tooltip>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Halt Badge
// ────────────────────────────────────────────────────────────────────────────
// Shows ONLY when this specific bot's symbol is in the haltedSymbols list.
// Provided by the new /api/account/alpaca/status endpoint (NASDAQ + NYSE
// LULD halts, fetched every 30s by the frontend).

export function HaltBadge({ symbol, haltedSymbols }) {
  if (!symbol || !Array.isArray(haltedSymbols) || haltedSymbols.length === 0) {
    return null;
  }
  const symUpper = String(symbol).toUpperCase();
  const haltInfo = haltedSymbols.find(h =>
    String(h.symbol || h).toUpperCase() === symUpper
  );
  if (!haltInfo) return null;

  // haltInfo may be a plain string symbol or {symbol, reason, resumed_at}
  const reason = typeof haltInfo === 'object' ? haltInfo.reason : null;
  const resumeTs = typeof haltInfo === 'object' ? haltInfo.resumed_at : null;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600}>{symUpper} is HALTED</Typography>
          {reason && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Reason: {reason}
            </Typography>
          )}
          {resumeTs && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Expected resume: {new Date(resumeTs).toLocaleTimeString()}
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
            Orders will be rejected until trading resumes.
          </Typography>
        </Box>
      }
      arrow
    >
      <Chip
        size="small"
        icon={<Block sx={{ fontSize: 14 }} />}
        label="HALTED"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: '#2a0a0a',
          color: '#d97b72',
          border: '1px solid #991b1b',
          animation: 'pulse-halt 2s infinite',
          '@keyframes pulse-halt': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.6 },
          },
          '& .MuiChip-icon': { color: '#d97b72' },
        }}
      />
    </Tooltip>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Buying Power Badge — replaces/augments the generic "Cash" stat for stocks
// ────────────────────────────────────────────────────────────────────────────
// Alpaca returns multiple cash-like figures:
//   buying_power:     usable for new entries (cash + margin + day-trade BP)
//   cash:             settled cash
//   regt_buying_power: Reg-T 2x buying power
//   daytrade_buying_power: 4x DT buying power (only PDT-flagged accounts)
//
// We show buying_power as the primary number (most useful for "can my bot
// open a position?") with the rest in a tooltip.

export function BuyingPowerChip({ buyingPower, cash, dtBuyingPower, equity }) {
  if (buyingPower == null && cash == null) return null;

  const bp = Number(buyingPower);
  const c = Number(cash);
  const dt = Number(dtBuyingPower);
  const eq = Number(equity);

  const fmt = (v) => Number.isFinite(v)
    ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

  return (
    <Tooltip
      title={
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="caption" fontWeight={600}>Alpaca Account</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption">Settled Cash:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{fmt(c)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Buying Power:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmt(bp)}</Typography>
          </Box>
          {Number.isFinite(dt) && dt > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">Day-Trade BP:</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{fmt(dt)}</Typography>
            </Box>
          )}
          {Number.isFinite(eq) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">Equity:</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{fmt(eq)}</Typography>
            </Box>
          )}
        </Box>
      }
      arrow
    >
      <Chip
        size="small"
        label={`BP: ${fmt(bp)}`}
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: '#1e2a4a',
          color: '#9cc3e4',
          border: '1px solid #4d8fc7',
          fontFamily: 'monospace',
        }}
      />
    </Tooltip>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const ms = typeof timestamp === 'number' ? timestamp : Date.parse(timestamp);
  if (!Number.isFinite(ms)) return '';
  const diffMs = ms - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 0) return `${Math.abs(diffMin)} min ago`;
  if (diffMin < 60) return `in ${diffMin} min`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `in ${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  return `in ${diffDay}d`;
}


// ────────────────────────────────────────────────────────────────────────────
// Combined wrapper — drop this into the bot card header for stock_equity bots
// ────────────────────────────────────────────────────────────────────────────
// Convenience component that renders all relevant stock badges in a row.
// Pass the bot plus the accountStatus blob from /api/account/alpaca/status.

export function StockStatusBadgeRow({ bot, accountStatus }) {
  if (!bot) return null;

  // Self-gate: only show badges for stock_equity bots
  let ti = {};
  try {
    ti = typeof bot.trade_info === 'string'
      ? JSON.parse(bot.trade_info || '{}')
      : (bot.trade_info || {});
  } catch { /* keep empty */ }

  const assetClass = ti.asset_class || bot.asset_class;
  if (assetClass !== 'stock_equity') return null;

  const acct = accountStatus || {};
  const symbol = ti.symbol || bot.symbol;

  return (
    <>
      <MarketHoursBadge
        marketStatus={acct.marketStatus}
        nextOpen={acct.nextOpen}
        nextClose={acct.nextClose}
      />
      <PdtBadge
        daytradeCount={acct.daytradeCount}
        pdtBlocked={acct.pdtBlocked}
        equity={acct.equity}
      />
      <HaltBadge
        symbol={symbol}
        haltedSymbols={acct.haltedSymbols}
      />
    </>
  );
}